<template>
  <b-sidebar
    id="release-notes-panel"
    title="Release Notes"
    right
    shadow
    backdrop
    bg-variant="white"
    width="480px"
    @shown="fetchVersions"
  >
    <div class="px-3 py-2">
      <ife-simple-select
        label="Version"
        :options="versionOptions"
        :value="selectedVersion"
        :loading="versionsLoading"
        :return-object="false"
        default-first-option-text="Select a version…"
        class="mb-3"
        @input="fetchNotes"
      />
      <div
        v-if="notesLoading"
        class="text-center py-4"
      >
        <b-spinner
          variant="secondary"
          label="Loading..."
        />
      </div>
      <div
        v-else-if="error"
        class="text-danger py-2"
      >
        {{ error }}
      </div>
      <div
        v-else-if="notes"
        class="release-notes-content"
        v-html="renderedNotes"
      />
      <div
        v-else-if="selectedVersion"
        class="text-muted py-2"
      >
        No release notes available for this version.
      </div>
    </div>
  </b-sidebar>
</template>

<script>
import {marked} from 'marked';
import IFESimpleSelect from '@/Client/Component/Common/Wrapper/IFESimpleSelect';

export default {
  name: 'ReleaseNotesSidePanel',
  components: {
    'ife-simple-select': IFESimpleSelect,
  },
  data() {
    return {
      versionOptions: [],
      selectedVersion: null,
      notes: null,
      versionsLoading: false,
      notesLoading: false,
      error: null,
    };
  },
  computed: {
    renderedNotes() {
      return this.notes && typeof this.notes === 'string' ? marked(this.notes) : '';
    },
  },
  methods: {
    addVersionOptions(versions) {
      this.versionOptions = [];

      for (const v of versions) {
        const version = v.name ?? '';
        const releaseDate = this.formatDate(v.releaseDate ?? '', 'DD-MMM-YY');
        this.versionOptions.push({id: version, name: `${version} - ${releaseDate}`});
      }

      const current = process.env.APP_VERSION;

      this.selectedVersion = this.versionOptions.some((o) => o.id === current) ?
          current :
          (this.versionOptions[0]?.id ?? null);

      if (this.selectedVersion) {
        this.fetchNotes(this.selectedVersion);
      }
    },
    fetchVersions() {
      if (this.versionOptions.length) return;
      this.versionsLoading = true;
      this.axios.get('/version/released')
          .then((resp) => {
            this.addVersionOptions(resp.data?.versions ?? []);
          })
          .catch(() => {
            this.error = 'Failed to load versions.';
          })
          .finally(() => {
            this.versionsLoading = false;
          });
    },
    fetchNotes(version) {
      this.selectedVersion = version;
      this.notes = null;
      this.error = null;
      this.notesLoading = true;
      this.axios.get(`/version/release-notes/${version}/markdown`)
          .then((resp) => {
            this.notes = resp.data;
          })
          .catch(() => {
            this.error = 'Failed to load release notes.';
          })
          .finally(() => {
            this.notesLoading = false;
          });
    },
  },
};
</script>

<style scoped>
.release-notes-content :deep(h1) {
  font-size: 1.4rem;
  margin-top: 0;
  margin-bottom: 1rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid #dee2e6;
}

.release-notes-content :deep(h2) {
  font-size: 1.15rem;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.release-notes-content :deep(h3) {
  font-size: 1rem;
  margin-top: 1rem;
  margin-bottom: 0.4rem;
  color: #6c757d;
}

.release-notes-content :deep(p) {
  margin-bottom: 0.75rem;
  line-height: 1.6;
}

.release-notes-content :deep(ul),
.release-notes-content :deep(ol) {
  padding-left: 1.25rem;
  margin-bottom: 0.75rem;
}

.release-notes-content :deep(li) {
  margin-bottom: 0.25rem;
  line-height: 1.5;
}

.release-notes-content :deep(code) {
  background-color: #f8f9fa;
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
  font-size: 0.875em;
}

.release-notes-content :deep(blockquote) {
  border-left: 3px solid #dee2e6;
  padding-left: 1rem;
  color: #6c757d;
  margin: 0.75rem 0;
}

.release-notes-content :deep(hr) {
  margin: 1.25rem 0;
}
</style>
